// Number / time formatting shared across pages.
//
// Peak multiples and market caps follow the A1 share cards exactly
// (web-admin/backend/app/services/card_renderer.py `_mult` / `_fmt_mcap`,
// tweet_studio.fmt_mult): a token shows the same number on X and here.
// Only the decimal mark is localised (K4: "8,6×" in Turkish).

type Lang = "en" | "tr";

function decimalMark(value: string, lang: Lang): string {
  return lang === "tr" ? value.replace(".", ",") : value;
}

// Turkish groups thousands with a dot, which reads as a decimal next to a
// multiple ("1.423×"). Four-digit values stay ungrouped in Turkish.
function groupInt(value: number, lang: Lang): string {
  if (lang === "tr") return value < 10_000 ? String(value) : value.toLocaleString("tr-TR");
  return value.toLocaleString("en-US");
}

/** Peak multiple, rounded down like the cards: ≥10 whole, below one decimal. */
export function fmtMultiple(n: number | null | undefined, lang: Lang = "en"): string {
  if (n == null || !isFinite(n) || n <= 0) return "—";
  if (n < 0.1) return `<${decimalMark("0.1", lang)}×`;
  // Never round a headline up across the next milestone.
  if (n >= 10) return `${groupInt(Math.floor(n + 1e-9), lang)}×`;
  return `${decimalMark((Math.floor(n * 10 + 1e-9) / 10).toFixed(1), lang)}×`;
}

/** Two-decimal ratio for model statistics (medians below 1× need the detail). */
export function fmtRatio(n: number | null | undefined, lang: Lang = "en"): string {
  if (n == null || !isFinite(n)) return "—";
  return `${decimalMark(n.toFixed(2), lang)}×`;
}

const USD_UNITS: [number, string][] = [[1e12, "T"], [1e9, "B"], [1e6, "M"], [1e3, "K"]];

function compactUsd(abs: number, lang: Lang): string {
  for (let i = 0; i < USD_UNITS.length; i++) {
    const [scale, unit] = USD_UNITS[i];
    if (abs < scale) continue;
    const scaled = Math.round((abs / scale) * 10) / 10;
    // 999,960 rounds to 1000.0K; show it in the next unit instead.
    if (scaled >= 1000 && i > 0) {
      const [upScale, upUnit] = USD_UNITS[i - 1];
      return `${decimalMark(String(Math.round((abs / upScale) * 10) / 10), lang)}${upUnit}`;
    }
    return `${decimalMark(String(scaled), lang)}${unit}`;
  }
  return decimalMark(String(Math.round(abs * 100) / 100), lang);
}

/** Market cap / liquidity: $9.9K, $361.2K, $3.8M (one decimal, trailing zero dropped). */
export function fmtUsd(n: number | null | undefined, lang: Lang = "en"): string {
  if (n == null || !isFinite(n)) return "—";
  return `${n < 0 ? "-" : ""}$${compactUsd(Math.abs(n), lang)}`;
}

// Signed USD for PnL figures. Used by the Strategy Lab (USD-denominated
// virtual PnL, chain-neutral).
export function fmtUsdSigned(n: number | null | undefined, lang: Lang = "en"): string {
  if (n == null || !isFinite(n)) return "—";
  const abs = Math.abs(n);
  // Garbage guard: a real virtual-PnL figure is never this large. Without
  // this, .toFixed() flips to scientific notation past 1e21 and the UI
  // rendered strings like "+$2.0955...e+43M" (2026-08-30 report). The
  // backend now filters the anomaly at source; this is defense in depth.
  if (abs >= 1e15) return "—";
  return `${n >= 0 ? "+" : "-"}$${compactUsd(abs, lang)}`;
}

export function fmtPct(n: number | null | undefined, decimals = 0, lang: Lang = "en"): string {
  if (n == null || !isFinite(n)) return "—";
  const value = decimalMark(n.toFixed(decimals), lang);
  return lang === "tr" ? `%${value}` : `${value}%`;
}

export function fmtCount(n: number | null | undefined, lang: Lang = "en"): string {
  if (n == null || !isFinite(n)) return "—";
  return Math.round(n).toLocaleString(lang === "tr" ? "tr-TR" : "en-US");
}

/** Ticker as printed on the cards: "$" + upper case. */
export function fmtSymbol(symbol: string | null | undefined): string {
  const clean = (symbol ?? "").trim().replace(/^\$+/, "").trim();
  return clean && clean !== "?" ? `$${clean.toUpperCase()}` : "—";
}

export function pnlColor(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return "text-slate-400";
  if (n > 0) return "text-emerald-400";
  if (n < 0) return "text-red-400";
  return "text-slate-400";
}

// Default English labels kept inline so callers without a labels arg
// (e.g. unit tests) get a sensible fallback. UI callers pass t.misc so
// the output respects the active language.
export interface TimeAgoLabels {
  just_now: string;
  min_ago: string;
  hr_ago: string;
  days_ago: string;
  unknown: string;
}

const DEFAULT_TIME_LABELS: TimeAgoLabels = {
  just_now: "just now",
  min_ago: "{n} min ago",
  hr_ago: "{n} hr ago",
  days_ago: "{n} days ago",
  unknown: "unknown",
};

export function timeAgo(
  iso: string | undefined | null,
  labels: TimeAgoLabels = DEFAULT_TIME_LABELS,
): string {
  if (!iso) return labels.unknown;
  const then = new Date(iso).getTime();
  if (!isFinite(then)) return labels.unknown;
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return labels.just_now;
  if (diffSec < 3600)
    return labels.min_ago.replace("{n}", String(Math.floor(diffSec / 60)));
  if (diffSec < 86400)
    return labels.hr_ago.replace("{n}", String(Math.floor(diffSec / 3600)));
  return labels.days_ago.replace("{n}", String(Math.floor(diffSec / 86400)));
}
